import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { Trophy, Crown, Medal, Coins, ArrowLeft, Users } from 'lucide-react';
import { toast } from 'sonner';

export function Ranking() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myStats, setMyStats] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadRanking();
    loadMyStats();
  }, [isAuthenticated, navigate]);

  const loadRanking = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/ranking?limit=50`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Erro ao carregar ranking');
      
      const data = await response.json();
      setRanking(data);
    } catch (error) {
      console.error('Failed to load ranking:', error);
      toast.error('Erro ao carregar ranking');
    } finally {
      setLoading(false);
    }
  };

  const loadMyStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/my-stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMyStats(data);
      }
    } catch (error) {
      console.error('Failed to load my stats:', error);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="h-8 w-8 text-yellow-500" />;
    if (rank === 2) return <Medal className="h-7 w-7 text-gray-400" />;
    if (rank === 3) return <Medal className="h-6 w-6 text-orange-600" />;
    return null;
  };

  const getRankBadgeColor = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-400 to-yellow-600';
    if (rank === 2) return 'bg-gradient-to-r from-gray-300 to-gray-500';
    if (rank === 3) return 'bg-gradient-to-r from-orange-400 to-orange-600';
    if (rank <= 10) return 'bg-gradient-to-r from-blue-500 to-purple-500';
    return 'bg-gray-200 text-gray-800';
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto" data-testid="ranking-page">
      <div className="mb-6">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Trophy className="h-7 w-7 text-amber-500" strokeWidth={1.5} />
            <h1 className="text-2xl font-bold">
              Ranking
              </h1>
            </div>
            <p className="text-xl text-gray-600">
              Top 50 usuários que mais convidaram amigos para a U-Clean
            </p>
          </div>
        </div>

        {/* Minha Posição */}
        {myStats && myStats.rank && (
          <Card className="mb-8 bg-gradient-to-r from-purple-500 to-blue-500 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 rounded-full">
                    <Users className="h-8 w-8" />
                  </div>
                  <div>
                    <div className="text-sm opacity-90">Sua Posição no Ranking</div>
                    <div className="text-3xl font-bold">#{myStats.rank}</div>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-sm opacity-90">Total de Indicações</div>
                  <div className="text-3xl font-bold">{myStats.successful_referrals}</div>
                </div>

                <div className="text-right">
                  <div className="text-sm opacity-90">Tokens Ganhos</div>
                  <div className="text-3xl font-bold flex items-center gap-2">
                    <Coins className="h-6 w-6" />
                    {myStats.tokens_earned}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Ranking List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown className="h-6 w-6 text-yellow-500" />
              Top 50 Fundadores
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ranking.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Trophy className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Ranking ainda vazio</p>
                <p className="text-sm mt-2">Seja o primeiro a convidar amigos!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {ranking.map((item) => {
                  const isCurrentUser = user && item.user_id === user.id;
                  
                  return (
                    <div
                      key={item.user_id}
                      className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                        isCurrentUser
                          ? 'bg-purple-50 border-purple-300 shadow-md'
                          : item.rank <= 3
                          ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200'
                          : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {/* Rank + Avatar */}
                      <div className="flex items-center gap-4 flex-1">
                        {/* Rank Badge */}
                        <div className="flex flex-col items-center min-w-[60px]">
                          {getRankIcon(item.rank)}
                          <div className={`mt-2 px-3 py-1 rounded-full font-bold text-white text-sm ${getRankBadgeColor(item.rank)}`}>
                            #{item.rank}
                          </div>
                        </div>

                        {/* Avatar */}
                        <Avatar className="h-14 w-14 border-2 border-white shadow-md">
                          <AvatarImage src={item.avatar_url} alt={item.name} />
                          <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white font-bold">
                            {getInitials(item.name)}
                          </AvatarFallback>
                        </Avatar>

                        {/* Nome + Badge Fundador */}
                        <div className="flex-1">
                          <div className="font-bold text-lg flex items-center gap-2">
                            {item.name}
                            {isCurrentUser && (
                              <Badge className="bg-purple-500 text-white">Você</Badge>
                            )}
                          </div>
                          {item.badge && (
                            <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-0 mt-1">
                              <Crown className="h-3 w-3 mr-1" />
                              {item.badge}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="flex gap-8 text-center">
                        <div>
                          <div className="text-sm text-gray-600">Indicações</div>
                          <div className="text-2xl font-bold text-blue-600">
                            {item.total_referrals}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600">Tokens</div>
                          <div className="text-2xl font-bold text-yellow-600 flex items-center gap-1">
                            <Coins className="h-5 w-5" />
                            {item.tokens_earned}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Card className="bg-gradient-to-r from-green-500 to-blue-500 text-white border-0">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold mb-4">Quer subir no ranking?</h3>
              <p className="text-lg mb-6">
                Convide seus amigos e ganhe +20 tokens por cada cadastro completo!
              </p>
              <Button
                onClick={() => navigate('/me')}
                className="bg-white text-blue-600 hover:bg-gray-100 font-bold px-8 py-6 text-lg"
              >
                Ver Meu Link de Convite
              </Button>
            </CardContent>
          </Card>
      </div>
    </div>
  );
}
